import { supabase } from '../lib/supabase';

export interface UserProfile {
  id: string;
  email: string;
  stripe_customer_id: string | null;
  subscription_status: 'rookie' | 'pro' | 'elite' | 'cancelled' | 'past_due';
  subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  practice_generation_count: number;
  game_plan_generation_count: number;
  ai_chat_message_count?: number;
  usage_period_start?: string | null;
  created_at: string;
  updated_at: string;
}

/** Rookie monthly limits (reset each calendar month). Pro has no cap. */
export const ROOKIE_LIMITS = {
  CHAT_MESSAGES: 20,
  PRACTICE_PLANS: 3,
  GAME_PLANS: 1,
} as const;

export interface SubscriptionPlans {
  rookie: {
    name: string;
    price: number;
    priceId: string;
    features: string[];
  };
  pro: {
    name: string;
    price: number;
    priceId: string;
    features: string[];
  };
  elite: {
    name: string;
    price: number;
    priceId: string;
    features: string[];
  };
}

export const SUBSCRIPTION_PLANS: SubscriptionPlans = {
  rookie: {
    name: 'Rookie',
    price: 0,
    priceId: '',
    features: [
      `${ROOKIE_LIMITS.CHAT_MESSAGES} AI Coach messages/month`,
      `${ROOKIE_LIMITS.PRACTICE_PLANS} AI-powered practice plans/month`,
      `${ROOKIE_LIMITS.GAME_PLANS} AI-powered game plan/month`,
      'Up to 1 team',
      'Lineup & depth charts',
      'Stat tracking',
      'Unlimited manual practice/drill creation',
      'Roster management',
      'Player Development'
    ],
  },
  pro: {
    name: 'Pro',
    price: 4.99,
    priceId: import.meta.env.VITE_STRIPE_PRO_PRICE_ID || '',
    features: [
      'Everything in Rookie, plus...',
      'Unlimited AI Coach messages',
      'Unlimited AI-powered practice planning',
      'Unlimited AI-powered game planning',
      'Multiple teams',
      'Lineup & depth charts with AI optimization',
      'Stat tracking w/ GameChanger import',
      'Advanced features (Practice duplication)',
      'Priority support'
      
    ],
  },
  elite: {
    name: 'Elite',
    price: 0,
    priceId: '',
    features: [
      'All Pro features',
      'Live game tracking',
      'Advanced statistics',
      'Real-time game management'
    ],
  },
};

function getCurrentMonthString(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

class SubscriptionService {
  /** Reset monthly usage counts when the calendar month changes. Call before reading/incrementing counts. */
  async ensureUsagePeriodResetted(userId: string): Promise<void> {
    const currentMonth = getCurrentMonthString();
    const { data: row } = await supabase
      .from('profiles')
      .select('usage_period_start')
      .eq('id', userId)
      .single();
    const periodStart = (row as { usage_period_start?: string | null } | null)?.usage_period_start;
    if (periodStart === currentMonth) return;
    await supabase
      .from('profiles')
      .update({
        usage_period_start: currentMonth,
        practice_generation_count: 0,
        game_plan_generation_count: 0,
        ai_chat_message_count: 0,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    await this.ensureUsagePeriodResetted(userId);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    // If profile doesn't exist, create one for existing users
    if (error && error.code === 'PGRST116') { // Not found error
      console.log('Profile not found, creating one for existing user...');
      
      // Get user email from auth
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.email) {
        console.error('Cannot create profile: user email not found');
        return null;
      }

      // Create profile for existing user
      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: userData.user.email,
          subscription_status: 'rookie'
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating user profile:', createError);
        return null;
      }

      return newProfile;
    }

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    return data;
  }

  async createCheckoutSession(userId: string, priceId?: string): Promise<string | null> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      // SECURITY: Get user's session token instead of anon key
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('User must be authenticated to create checkout session');
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // SECURITY: Use user's session token, not anon key
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId,
          priceId: priceId || SUBSCRIPTION_PLANS.pro.priceId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      return url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  }

  async createPortalSession(userId: string): Promise<string | null> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      // SECURITY: Get user's session token instead of anon key
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('User must be authenticated to access billing portal');
      }
      
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // SECURITY: Use user's session token, not anon key
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const { url } = await response.json();
      return url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      return null;
    }
  }

  async subscribeToPro(userId: string): Promise<boolean> {
    const url = await this.createCheckoutSession(userId);
    if (url) {
      window.location.href = url;
      return true;
    }
    return false;
  }

  /**
   * After Stripe checkout success, the user is redirected back with ?session_id=...
   * Call this to sync the subscription from Stripe to the profile (in case the webhook
   * hasn't run yet or isn't configured, e.g. local dev).
   */
  async syncSubscriptionFromSession(sessionId: string): Promise<boolean> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return false;
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-sync-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      return data?.success === true;
    } catch {
      return false;
    }
  }

  async openBillingPortal(userId: string): Promise<boolean> {
    const url = await this.createPortalSession(userId);
    if (url) {
      window.location.href = url;
      return true;
    }
    return false;
  }

  /** Cancel Pro subscription at end of billing period; user reverts to Rookie when period ends. */
  async cancelSubscription(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        return { success: false, error: 'User must be authenticated to cancel subscription' };
      }
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: (data as { error?: string }).error || 'Failed to cancel subscription' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to cancel subscription' };
    }
  }

  isProUser(profile: UserProfile | null): boolean {
    return profile?.subscription_status === 'pro';
  }

  isSubscriptionActive(profile: UserProfile | null): boolean {
    if (!profile) return false;
    return profile.subscription_status === 'pro' || profile.subscription_status === 'rookie' || profile.subscription_status === 'elite';
  }

  getSubscriptionStatusDisplay(status: string): string {
    switch (status) {
      case 'pro':
        return 'Pro (Active)';
      case 'rookie':
      case 'cancelled':
        return 'Rookie (Free)';
      case 'elite':
        return 'Elite';
      case 'past_due':
        return 'Past Due';
      default:
        return 'Unknown';
    }
  }

  // Check if user has reached practice generation limit (Rookie: 3/month)
  hasReachedPracticeLimit(profile: UserProfile | null): boolean {
    if (!profile) return false;
    if (this.isProUser(profile)) return false;
    return (profile.practice_generation_count || 0) >= ROOKIE_LIMITS.PRACTICE_PLANS;
  }

  // Check if user has reached game plan generation limit (Rookie: 1/month)
  hasReachedGamePlanLimit(profile: UserProfile | null): boolean {
    if (!profile) return false;
    if (this.isProUser(profile)) return false;
    return (profile.game_plan_generation_count || 0) >= ROOKIE_LIMITS.GAME_PLANS;
  }

  // Check if user has reached AI Coach chat limit (Rookie: 20/month)
  hasReachedChatLimit(profile: UserProfile | null): boolean {
    if (!profile) return false;
    if (this.isProUser(profile)) return false;
    return (profile.ai_chat_message_count ?? 0) >= ROOKIE_LIMITS.CHAT_MESSAGES;
  }

  // Get practice generation count (current month)
  getPracticeGenerationCount(profile: UserProfile | null): number {
    if (!profile) return 0;
    return profile.practice_generation_count || 0;
  }

  // Get game plan generation count (current month)
  getGamePlanGenerationCount(profile: UserProfile | null): number {
    if (!profile) return 0;
    return profile.game_plan_generation_count || 0;
  }

  // Get AI chat message count (current month)
  getAiChatMessageCount(profile: UserProfile | null): number {
    if (!profile) return 0;
    return profile.ai_chat_message_count ?? 0;
  }

  // Increment practice generation count (monthly; period reset is done in ensureUsagePeriodResetted via getUserProfile)
  async incrementPracticeGenerationCount(userId: string): Promise<void> {
    try {
      await this.ensureUsagePeriodResetted(userId);
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('practice_generation_count')
        .eq('id', userId)
        .single();

      if (fetchError) {
        if (fetchError.code === '42703') {
          console.warn('practice_generation_count column not found, skipping increment');
          return;
        }
        console.error('Error fetching practice generation count:', fetchError);
        throw fetchError;
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          practice_generation_count: (profile?.practice_generation_count || 0) + 1,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        console.error('Error incrementing practice generation count:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in incrementPracticeGenerationCount:', error);
    }
  }

  // Increment game plan generation count (monthly)
  async incrementGamePlanGenerationCount(userId: string): Promise<void> {
    try {
      await this.ensureUsagePeriodResetted(userId);
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('game_plan_generation_count')
        .eq('id', userId)
        .single();

      if (fetchError) {
        if (fetchError.code === '42703') {
          console.warn('game_plan_generation_count column not found, skipping increment');
          return;
        }
        console.error('Error fetching game plan generation count:', fetchError);
        throw fetchError;
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          game_plan_generation_count: (profile?.game_plan_generation_count || 0) + 1,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        console.error('Error incrementing game plan generation count:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in incrementGamePlanGenerationCount:', error);
    }
  }

  // Increment AI chat message count (monthly, Rookie limit 20)
  async incrementAiChatMessageCount(userId: string): Promise<void> {
    try {
      await this.ensureUsagePeriodResetted(userId);
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('ai_chat_message_count')
        .eq('id', userId)
        .single();

      if (fetchError) {
        if (fetchError.code === '42703') {
          console.warn('ai_chat_message_count column not found, skipping increment');
          return;
        }
        console.error('Error fetching ai_chat_message_count:', fetchError);
        throw fetchError;
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          ai_chat_message_count: (profile?.ai_chat_message_count ?? 0) + 1,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        console.error('Error incrementing ai_chat_message_count:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in incrementAiChatMessageCount:', error);
    }
  }
}

export const subscriptionService = new SubscriptionService(); 